<template>
  <div class="navbar-wrapper" ref="navbarWrapper">
    <header class="navbar glass">
      <div class="navbar-container">
        <!-- Mobile menu button -->
        <button class="mobile-menu-btn hide-desktop" @click="toggleMobileMenu">
          <i :class="['pi', mobileMenuOpen ? 'pi-times' : 'pi-bars']"></i>
        </button>

        <!-- Logo & Brand Dropdown -->
        <div
          class="brand-menu-container"
          @mouseenter="brandMenuOpen = true"
          @mouseleave="brandMenuOpen = false"
        >
          <div class="brand-trigger">
            <RouterLink to="/" class="navbar-logo" @click="closeBrandMenu">
              <img src="/logo.svg" alt="MonDAI Logo" class="navbar-logo-img" />
              <span class="logo-text">
                <span class="logo-accent">{{ appName.split(" ")[0] }}</span>
                {{ appName.split(" ").slice(1).join(" ") }}
              </span>
            </RouterLink>
            <button
              class="brand-chevron-btn hide-mobile"
              @click="toggleBrandMenu"
              aria-label="Toggle brand menu"
            >
              <i :class="['pi', 'pi-chevron-down', { 'rotated': brandMenuOpen }]"></i>
            </button>
          </div>

          <!-- Brand Dropdown -->
          <Transition name="dropdown">
            <div v-if="brandMenuOpen" class="brand-dropdown glass">
              <RouterLink
                to="/"
                class="dropdown-item"
                @click="closeBrandMenu"
              >
                <i class="pi pi-home"></i>
                <span>Dashboard</span>
              </RouterLink>
              <RouterLink
                to="/chat"
                class="dropdown-item"
                @click="closeBrandMenu"
              >
                <i class="pi pi-sparkles"></i>
                <span>AI Assistant</span>
              </RouterLink>
            </div>
          </Transition>
        </div>

        <!-- Navigation Links (Desktop) -->
        <nav class="navbar-nav hide-mobile">
          <!-- EasyNav UI: Displayed inside Top Navbar when EasyNav is enabled -->
          <template v-if="easyNav.isEnabled.value">
            <!-- Home button to return to the active persona path landing page or Choose path -->
            <RouterLink
              :to="easyNavHomeLink"
              class="nav-link easynav-home-btn"
              title="Return to Path Home"
            >
              <i class="pi pi-home"></i>
            </RouterLink>

            <span class="navbar-nav-divider"></span>

            <!-- Submenu Path Links -->
            <template v-if="easyNav.persona.value">
              <RouterLink
                v-for="link in activeNavLinks"
                :key="link.to"
                :to="link.to"
                class="nav-link"
                :class="[
                  { active: isActive(link.to) },
                  `persona-${easyNav.persona.value}`
                ]"
              >
                <i :class="['pi', link.icon]"></i>
                <span>{{ link.label }}</span>
              </RouterLink>
            </template>
            <template v-else>
              <RouterLink
                v-for="link in navLinks"
                :key="link.to"
                :to="link.to"
                class="nav-link"
                :class="{ active: isActive(link.to) }"
              >
                <i :class="['pi', link.icon]"></i>
                <span>{{ link.label }}</span>
              </RouterLink>
            </template>
          </template>

          <!-- Standard Navigation Links: Displayed only when EasyNav is disabled -->
          <template v-else>
            <RouterLink
              v-for="link in navLinks"
              :key="link.to"
              :to="link.to"
              class="nav-link"
              :class="{ active: isActive(link.to) }"
            >
              <i :class="['pi', link.icon]"></i>
              <span>{{ link.label }}</span>
            </RouterLink>
          </template>
        </nav>

        <!-- Right side actions -->
        <div class="navbar-actions">
          <ThemeToggle />

          <!-- User menu -->
          <div class="user-menu" v-if="isAuthenticated">
            <button class="user-menu-trigger" @click="toggleUserMenu">
              <div class="user-avatar">
                <img
                  v-if="profile?.avatar_url"
                  :src="profile.avatar_url"
                  :alt="profile?.name || 'User'"
                />
                <i v-else class="pi pi-user"></i>
              </div>
              <span class="user-name hide-mobile">{{
                profile?.name || user?.email
              }}</span>
              <i class="pi pi-chevron-down hide-mobile"></i>
            </button>

            <!-- Dropdown -->
            <Transition name="dropdown">
              <div v-if="userMenuOpen" class="user-dropdown glass">
                <div class="user-dropdown-header">
                  <div class="user-dropdown-identity">
                    <span class="user-dropdown-name">{{ profile?.name || 'Community Member' }}</span>
                    <span class="user-dropdown-email">{{ user?.email }}</span>
                  </div>
                  <div class="user-dropdown-roles" v-if="userRoleBadges.length">
                    <span
                      v-for="r in userRoleBadges"
                      :key="r.id"
                      :class="['dropdown-role-tag', r.id]"
                    >
                      <i :class="r.icon"></i>
                      {{ r.label }}
                    </span>
                  </div>
                </div>
                <hr class="dropdown-divider" />

                <RouterLink
                  to="/profile"
                  class="dropdown-item"
                  @click="closeUserMenu"
                >
                  <i class="pi pi-user"></i>
                  <span>My Profile</span>
                </RouterLink>
                <RouterLink
                  to="/feedback"
                  class="dropdown-item"
                  @click="closeUserMenu"
                >
                  <i class="pi pi-question-circle"></i>
                  <span>Feedback & Support</span>
                </RouterLink>
                <RouterLink
                  v-if="isCuratorOrAdmin"
                  to="/submissions"
                  class="dropdown-item"
                  @click="closeUserMenu"
                >
                  <i class="pi pi-inbox"></i>
                  <span>Manage Submissions</span>
                  <span v-if="pendingCount > 0" class="nav-badge">{{
                    pendingCount
                  }}</span>
                </RouterLink>
                <RouterLink
                  v-if="isAdmin"
                  to="/admin"
                  class="dropdown-item"
                  @click="closeUserMenu"
                >
                  <i class="pi pi-cog"></i>
                  <span>Admin Panel</span>
                </RouterLink>

                <!-- Reselect Role / EasyNav Settings under user dropdown -->
                <hr class="dropdown-divider" />
                <div class="dropdown-section-title">Navigation Path</div>
                <button
                  v-for="roleOpt in roleOptions"
                  :key="roleOpt.value"
                  class="dropdown-item flex items-center justify-between"
                  :class="{ active: easyNav.persona.value === roleOpt.value && easyNav.isEnabled.value }"
                  @click="selectEasyNavPersona(roleOpt.value); closeUserMenu()"
                >
                  <span class="flex items-center gap-xs">
                    <i :class="['pi', roleOpt.icon]"></i>
                    <span>{{ roleOpt.label }}</span>
                  </span>
                  <i v-if="easyNav.persona.value === roleOpt.value && easyNav.isEnabled.value" class="pi pi-check text-xs text-accent"></i>
                </button>
                <button class="dropdown-item" @click="handleChoosePath(); closeUserMenu()">
                  <i class="pi pi-th-large"></i>
                  <span>Choose Path...</span>
                </button>
                <button class="dropdown-item" @click="toggleEasyNav(); closeUserMenu()">
                  <i class="pi pi-sync"></i>
                  <span>{{ easyNav.isEnabled.value ? 'Use Full Menu' : 'Use Easy Menu' }}</span>
                </button>
                <hr class="dropdown-divider" />
                <button class="dropdown-item" @click="handleSaveDefaultPage">
                  <i class="pi pi-bookmark"></i>
                  <span>Save current page as default</span>
                </button>
                <button v-if="hasCustomDefaultPage" class="dropdown-item" @click="handleClearDefaultPage">
                  <i class="pi pi-bookmark-fill"></i>
                  <span>Clear default page</span>
                </button>
                <hr class="dropdown-divider" />
                <button class="dropdown-item" @click="handleSignOut">
                  <i class="pi pi-sign-out"></i>
                  <span>Sign Out</span>
                </button>
              </div>
            </Transition>
          </div>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <Transition name="mobile-nav">
        <nav v-if="mobileMenuOpen" class="mobile-nav hide-desktop">
          <!-- Main App navigation links prepended on mobile -->
          <RouterLink
            to="/"
            class="mobile-nav-link"
            @click="closeMobileMenu"
          >
            <i class="pi pi-home"></i>
            <span>Dashboard</span>
          </RouterLink>
          <RouterLink
            to="/chat"
            class="mobile-nav-link"
            @click="closeMobileMenu"
          >
            <i class="pi pi-sparkles"></i>
            <span>AI Assistant</span>
          </RouterLink>
          
          <!-- Mobile View Switch Link -->
          <button
            v-if="easyNav.persona.value"
            class="mobile-nav-link text-accent flex items-center justify-between"
            @click="toggleEasyNav(); closeMobileMenu()"
          >
            <span class="flex items-center gap-xs">
              <i class="pi pi-sync"></i>
              <span>Use {{ easyNav.isEnabled.value ? 'Full Menu' : 'Easy Menu' }}</span>
            </span>
            <span class="text-xs text-muted" v-if="easyNav.isEnabled.value">
              🎯 {{ easyNav.persona.value }}
            </span>
          </button>

          <hr class="mobile-nav-divider" />

          <RouterLink
            v-for="link in activeNavLinks"
            :key="link.to"
            :to="link.to"
            class="mobile-nav-link"
            @click="closeMobileMenu"
          >
            <i :class="['pi', link.icon]"></i>
            <span>{{ link.label }}</span>
          </RouterLink>
        </nav>
      </Transition>
    </header>

    <!-- Global Message Banner -->
    <Transition name="banner">
      <div v-if="navbarMessage" class="navbar-banner-container">
        <div class="navbar-banner glass">
          <i class="pi pi-info-circle"></i>
          <span v-html="navbarMessage"></span>
        </div>
      </div>
    </Transition>

    <!-- Premium Toast notification -->
    <Transition name="toast-fade">
      <div v-if="toast.show" class="premium-toast-notification glass">
        <i class="pi pi-check-circle"></i>
        <span>{{ toast.message }}</span>
      </div>
    </Transition>
  </div>

  <!-- Backdrop for dropdowns -->
  <div v-if="userMenuOpen || brandMenuOpen" class="backdrop" @click="closeAllMenus"></div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth.store";
import { useUiStore } from "@/stores/ui.store";
import { useContentStore } from "@/stores/content.store";
import { useSubmissionsStore } from "@/stores/submissions.store";
import { config } from "@/config";
import ThemeToggle from "./ThemeToggle.vue";
import { useEasyNav } from "@/composables/useEasyNav";
import { resetInitialRedirect } from "@/guards/auth.guard";
import { UserRole } from "@/types/auth.types";

const easyNav = useEasyNav();

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();
const uiStore = useUiStore();
const contentStore = useContentStore();

const appName = config.app.name;

const easyNavHomeLink = computed(() => {
  if (easyNav.savedPersona.value) {
    return `/easy-nav/${easyNav.savedPersona.value}`;
  }
  return '/easy-nav';
});

const navbarMessage = computed(() => {
  const item = contentStore.getContentByKey("global", "navbar_banner");
  return item?.description || "";
});

const navLinks = [
  { to: "/connect", label: "Connect", icon: "pi-users" },
  { to: "/events", label: "Events", icon: "pi-calendar" },
  { to: "/education", label: "Learn", icon: "pi-book" },
  { to: "/earn", label: "Earn", icon: "pi-dollar" },
  { to: "/chapters", label: "Chapters", icon: "pi-map-marker" },
  { to: "/guide", label: "Guide", icon: "pi-question-circle" },
];

const easyNavOrganizerLinks = [
  { to: "/easy-nav/organize", label: "Organize Meetup", icon: "pi-calendar-plus", persona: "organizer" },
  { to: "/chapters", label: "Grow Members", icon: "pi-chart-line", persona: "organizer" },
  { to: "/easy-nav/connect", label: "Engage Members", icon: "pi-comments", persona: "organizer" },
  { to: "/easy-nav/funding", label: "Funding Options", icon: "pi-wallet", persona: "organizer" },
];

const easyNavMemberLinks = [
  { to: "/education", label: "Learn", icon: "pi-book", persona: "member" },
  { to: "/earn", label: "Earn", icon: "pi-dollar", persona: "member" },
  { to: "/easy-nav/connect", label: "Connect", icon: "pi-users", persona: "member" },
  { to: "/events", label: "Events", icon: "pi-calendar", persona: "member" },
];

const easyNavPartnerLinks = [
  { to: "/easy-nav/partner-form", label: "Get Started", icon: "pi-star", persona: "partner" },
  { to: "/chapters", label: "Find Events", icon: "pi-map-marker", persona: "partner" },
  { to: "/easy-nav/partner/connect", label: "Connect", icon: "pi-users", persona: "partner" },
];

const activeNavLinks = computed(() => {
  if (!easyNav.isEnabled.value || !easyNav.persona.value) {
    return navLinks;
  }
  switch (easyNav.persona.value) {
    case "organizer":
      return easyNavOrganizerLinks;
    case "member":
      return easyNavMemberLinks;
    case "partner":
      return easyNavPartnerLinks;
    default:
      return navLinks;
  }
});

async function toggleEasyNav() {
  const nextVal = !easyNav.isEnabled.value;
  await easyNav.toggle(nextVal);
  if (nextVal) {
    if (easyNav.persona.value) {
      router.push(`/easy-nav/${easyNav.persona.value}`);
    } else {
      router.push('/easy-nav');
    }
  } else {
    router.push('/connect');
  }
}

const roleMenuOpen = ref(false);

const roleOptions = [
  { value: "member" as const, label: "Community", icon: "pi-users" },
  { value: "organizer" as const, label: "Organizer", icon: "pi-calendar-plus" },
  { value: "partner" as const, label: "Partner / Sponsor", icon: "pi-briefcase" },
];


async function selectEasyNavPersona(personaValue: 'member' | 'organizer' | 'partner') {
  localStorage.removeItem("mondai-last-visited-url");
  await easyNav.setPersona(personaValue, true);
  await easyNav.toggle(true);
  roleMenuOpen.value = false;
  // Redirect to corresponding landing page or path
  router.push(`/easy-nav/${personaValue}`);
}

async function handleChoosePath() {
  localStorage.removeItem("mondai-last-visited-url");
  await easyNav.setPersona(null, true);
  await easyNav.toggle(true);
  roleMenuOpen.value = false;
  router.push('/easy-nav');
}

const userMenuOpen = ref(false);
const brandMenuOpen = ref(false);
const mobileMenuOpen = computed(() => uiStore.mobileMenuOpen);
const navbarWrapper = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;

const isAuthenticated = computed(() => authStore.isAuthenticated);
const user = computed(() => authStore.user);
const profile = computed(() => authStore.profile);
const isAdmin = computed(() => authStore.isAdmin);
const isCuratorOrAdmin = computed(
  () => authStore.isAdmin || authStore.isCurator,
);

const userRoleBadges = computed(() => {
  const roles = (authStore.userRoles || [UserRole.MEMBER]) as UserRole[];
  const list: Array<{ id: string; label: string; icon: string }> = [];
  if (roles.includes(UserRole.ADMINISTRATOR)) {
    list.push({ id: 'admin', label: 'Admin', icon: 'pi pi-shield' });
  }
  if (roles.includes(UserRole.CHAPTER_LEAD)) {
    list.push({ id: 'chapter_lead', label: 'Chapter Lead', icon: 'pi pi-compass' });
  }
  if (roles.includes(UserRole.CURATOR)) {
    list.push({ id: 'curator', label: 'Curator', icon: 'pi pi-bookmark' });
  }
  if (roles.includes(UserRole.MEMBER) || list.length === 0) {
    list.push({ id: 'member', label: 'Member', icon: 'pi pi-user' });
  }
  return list;
});



const submissionsStore = useSubmissionsStore();
const pendingCount = computed(() => submissionsStore.pendingCount);

function isActive(path: string) {
  return route.path === path || route.path.startsWith(path + "/");
}

function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value;
}

function closeUserMenu() {
  userMenuOpen.value = false;
}

function toggleBrandMenu() {
  brandMenuOpen.value = !brandMenuOpen.value;
}

function closeBrandMenu() {
  brandMenuOpen.value = false;
}

function closeAllMenus() {
  userMenuOpen.value = false;
  brandMenuOpen.value = false;
  roleMenuOpen.value = false;
}

function toggleMobileMenu() {
  uiStore.toggleMobileMenu();
}

function closeMobileMenu() {
  uiStore.closeMobileMenu();
}

async function handleSignOut() {
  closeUserMenu();
  resetInitialRedirect();
  await authStore.signOut();
  // Force redirect to login page
  router.push({ name: "login" });
}

const customDefaultPage = ref<string | null>(localStorage.getItem("mondai-default-opening-url"));
const hasCustomDefaultPage = computed(() => !!customDefaultPage.value);

const toast = ref({ show: false, message: "", type: "success" });
function showToast(message: string, type: "success" | "error" = "success") {
  toast.value = { show: true, message, type };
  setTimeout(() => {
    toast.value.show = false;
  }, 3000);
}

function getPageDisplayName(): string {
  const path = route.path;
  if (path === "/") return "Dashboard";
  if (path.startsWith("/chat")) return "AI Assistant";
  if (path.startsWith("/connect")) {
    const tab = route.query.tab;
    if (tab === "connections") return "Connect - My Connections";
    if (tab === "groups") return "Connect - Groups";
    if (tab === "attendees") return "Connect - Event Attendees";
    if (tab === "imports") return "Connect - Import";
    return "Connect - Search Members";
  }
  if (path.startsWith("/events")) {
    const hash = route.hash.replace("#", "");
    if (hash === "featured") return "Events - Featured Guest";
    if (hash === "submitted") return "Events - Submitted";
    if (hash === "past") return "Events - Recent";
    if (hash === "archive") return "Events - Archive";
    return "Events - Global";
  }
  if (path.startsWith("/education")) {
    const filter = route.query.filter;
    if (filter === "recent") return "Learn - Recent";
    if (filter === "all") return "Learn - All";
    if (filter) return `Learn - ${String(filter)}`;
    return "Learn";
  }
  if (path.startsWith("/earn")) {
    const filter = route.query.filter;
    if (filter === "recent") return "Earn - Recent";
    if (filter === "all") return "Earn - All";
    if (filter) return `Earn - ${String(filter)}`;
    return "Earn";
  }
  if (path.startsWith("/chapters")) {
    const tab = route.query.tab;
    if (tab === "my-chapter") return "Chapters - My Chapter";
    if (tab === "resources") return "Chapters - Resources";
    if (tab === "communications") return "Chapters - Communications";
    if (tab === "submit-update") return "Chapters - Submit Update";
    return "Chapters - Chapters List";
  }
  if (path.startsWith("/profile")) return "My Profile";
  if (path.startsWith("/feedback")) return "Feedback & Support";
  if (path.startsWith("/admin")) {
    const hash = route.hash.replace("#", "");
    if (hash) return `Admin Panel - ${hash}`;
    return "Admin Panel";
  }
  return path;
}

function handleSaveDefaultPage() {
  const currentFullPath = route.fullPath;
  localStorage.setItem("mondai-default-opening-url", currentFullPath);
  customDefaultPage.value = currentFullPath;
  closeUserMenu();
  showToast(`Saved "${getPageDisplayName()}" as default landing page!`, "success");
}

function handleClearDefaultPage() {
  localStorage.removeItem("mondai-default-opening-url");
  customDefaultPage.value = null;
  closeUserMenu();
  showToast("Cleared custom default landing page. Reset to Dashboard.", "success");
}

// Close menu on escape
function handleEscape(e: KeyboardEvent) {
  if (e.key === "Escape") {
    closeUserMenu();
    closeMobileMenu();
    closeBrandMenu();
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleEscape);

  if (navbarWrapper.value) {
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Use margin-box height equivalent
        const height = entry.target.getBoundingClientRect().height;
        document.documentElement.style.setProperty(
          "--nav-height",
          `${height}px`,
        );
      }
    });
    resizeObserver.observe(navbarWrapper.value);
  }
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleEscape);
  resizeObserver?.disconnect();
});
</script>

<style scoped>
.navbar-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: none;
  padding-top: var(--spacing-md);
  gap: var(--spacing-sm);
  padding-bottom: var(--spacing-sm);
}

.navbar {
  position: relative;
  z-index: 2;
  width: calc(100% - var(--spacing-xl) * 2);
  max-width: 1200px;
  /* height: var(--header-height); */
  border-radius: var(--radius-pill);
}

.navbar-banner-container {
  display: flex;
  justify-content: center;
  padding: 0 var(--spacing-md);
  width: 100%;
  position: relative;
  z-index: 1;
}

.navbar-banner {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs) var(--spacing-lg);
  border-radius: var(--radius-pill);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  max-width: fit-content;
  pointer-events: auto;
  box-shadow: var(--shadow-sm);
}

.navbar-banner i {
  color: var(--color-accent);
  font-size: 0.85rem;
}

.navbar-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 100%;
  padding: 0 var(--spacing-lg);
  pointer-events: auto;
}

.mobile-menu-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  transition: background var(--transition-fast);
}

.mobile-menu-btn:hover {
  background: var(--color-bg-tertiary);
}

/* Brand Dropdown & Logo Styling */
.brand-menu-container {
  position: relative;
  display: flex;
  flex-direction: column;
  pointer-events: auto; /* Ensure hover behaves correctly */
}

.brand-trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.navbar-logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  text-decoration: none;
  transition: transform var(--transition-fast);
}

.navbar-logo-img {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.brand-menu-container:hover .navbar-logo {
  transform: scale(1.02);
}

.logo-text {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
}

.logo-accent {
  color: var(--color-accent);
  transition: color var(--transition-fast);
}

.brand-menu-container:hover .logo-accent {
  color: var(--color-accent-dark);
}

.brand-chevron-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--spacing-xs);
  color: var(--color-text-muted);
  cursor: pointer;
  background: none;
  border: none;
}

.brand-chevron-btn i {
  font-size: 0.8rem;
  transition: transform var(--transition-fast), color var(--transition-fast);
}

.brand-chevron-btn i.rotated {
  transform: rotate(180deg);
}

.brand-menu-container:hover .brand-chevron-btn i {
  color: var(--color-accent);
}

.brand-dropdown {
  position: absolute;
  top: calc(100% + var(--spacing-sm));
  left: 0;
  min-width: 180px;
  padding: var(--spacing-sm);
  border-radius: var(--radius-lg);
  z-index: var(--z-dropdown);
  /* Matching standard dropdown appearance */
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-strong);
  pointer-events: auto;
  box-shadow: var(--shadow-lg);
}

.brand-dropdown::before {
  content: '';
  position: absolute;
  top: calc(-1 * var(--spacing-sm) - 2px);
  left: 0;
  right: 0;
  height: calc(var(--spacing-sm) + 4px);
  background: transparent;
}

/* Mobile Nav Divider */
.mobile-nav-divider {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--spacing-sm) 0;
}

.navbar-nav {
  display: flex;
  align-items: center;
  gap: var(--spacing-2xs);
}

.nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  border-radius: var(--radius-pill);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  transition: all var(--transition-fast);
}

.nav-link:hover {
  color: var(--color-text-primary);
  background: var(--color-bg-tertiary);
}

.nav-link.active {
  color: var(--color-accent);
  background: var(--color-accent-subtle);
  position: relative;
}

.nav-link.active::after {
  content: "";
  position: absolute;
  bottom: 4px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 3px;
  background: var(--color-accent);
  border-radius: var(--radius-full);
}

.navbar-actions {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
}

.user-menu {
  position: relative;
}

.user-menu-trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-xs);
  padding-right: var(--spacing-md);
  border-radius: var(--radius-pill);
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.user-menu-trigger:hover {
  border-color: var(--color-accent);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: var(--color-bg-elevated);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-name {
  font-weight: var(--font-weight-medium);
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-header {
  padding: var(--spacing-sm) var(--spacing-md);
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.user-dropdown-identity {
  display: flex;
  flex-direction: column;
}

.user-dropdown-name {
  font-weight: var(--font-weight-bold);
  font-size: var(--font-size-sm);
  color: var(--color-text-primary);
}

.user-dropdown-email {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
}

.dropdown-role-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  padding: 1px 6px;
  border-radius: var(--radius-full);
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.dropdown-role-tag.chapter_lead {
  background: rgba(156, 39, 176, 0.15);
  color: #ab47bc;
  border: 1px solid rgba(156, 39, 176, 0.35);
}

.dropdown-role-tag.admin {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.dropdown-role-tag.curator {
  background: rgba(139, 92, 246, 0.15);
  color: #8b5cf6;
  border: 1px solid rgba(139, 92, 246, 0.35);
}

.dropdown-role-tag.member {
  background: var(--color-bg-tertiary);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
}

.user-dropdown {
  position: absolute;
  top: calc(100% + var(--spacing-sm));
  right: 0;
  min-width: 200px;
  padding: var(--spacing-sm);
  border-radius: var(--radius-lg);
  z-index: var(--z-dropdown);
  /* Use more opaque background than standard glass for dropdowns */
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-strong);
  pointer-events: auto;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  transition: background var(--transition-fast);
  width: 100%;
  text-align: left;
}

.dropdown-item:hover {
  background: var(--color-bg-tertiary);
}

.dropdown-divider {
  border: none;
  border-top: 1px solid var(--color-border);
  margin: var(--spacing-sm) 0;
}

.nav-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: var(--color-accent);
  color: white;
  border-radius: var(--radius-pill);
  font-size: 0.65rem;
  font-weight: var(--font-weight-bold);
  margin-left: auto;
}

.backdrop {
  position: fixed;
  inset: 0;
  z-index: calc(var(--z-dropdown) - 1);
}

/* Mobile Navigation */
.mobile-nav {
  position: absolute;
  top: calc(100% + var(--spacing-sm));
  left: var(--spacing-md);
  right: var(--spacing-md);
  background: var(--color-bg-elevated);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-lg);
  padding: var(--spacing-sm);
  box-shadow: var(--shadow-lg);
  pointer-events: auto;
}

.mobile-nav-link {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
  color: var(--color-text-primary);
  text-decoration: none;
  font-weight: var(--font-weight-medium);
}

.mobile-nav-link:hover {
  background: var(--color-bg-tertiary);
}

/* Transitions */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: all 0.2s ease;
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  opacity: 0;
  transform: translateY(-16px);
}

.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Mobile adjustments */
@media (max-width: 1100px) {
  .nav-link {
    padding: var(--spacing-xs) var(--spacing-sm);
    gap: var(--spacing-xs);
    font-size: var(--font-size-sm);
  }
  .navbar-nav {
    gap: 2px;
  }
  .logo-text {
    font-size: var(--font-size-lg);
  }
  .user-name,
  .user-menu-trigger .pi-chevron-down {
    display: none;
  }
}

@media (max-width: 768px) {
  .navbar-wrapper {
    padding-top: var(--spacing-sm);
  }

  .navbar {
    width: calc(100% - var(--spacing-md) * 2);
    border-radius: var(--radius-lg);
  }

  .user-menu-trigger {
    padding-right: var(--spacing-xs);
  }
}

/* Premium Toast Styles */
.premium-toast-notification {
  position: fixed;
  bottom: var(--spacing-lg);
  right: var(--spacing-lg);
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md) var(--spacing-lg);
  background: rgba(26, 36, 43, 0.85); /* Sleek glass backdrop */
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  color: var(--color-text);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
}
.premium-toast-notification i {
  color: var(--color-success);
  font-size: var(--font-size-md);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

/* EasyNav selector styles */
.easynav-selector-container {
  position: relative;
  margin-right: var(--spacing-sm);
}

.easynav-role-trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-pill);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  background: var(--color-bg-tertiary);
  border: 1.5px solid var(--color-border);
  color: var(--color-text-secondary);
  transition: all var(--transition-fast);
  outline: none;
}

.easynav-role-trigger:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-primary);
}

/* Active personas in trigger */
.easynav-role-trigger.persona-organizer.active {
  background: var(--color-accent-subtle);
  border-color: var(--color-accent);
  color: var(--color-accent);
}
.easynav-role-trigger.persona-member.active {
  background: rgba(124, 58, 237, 0.1);
  border-color: var(--color-persona-member);
  color: var(--color-persona-member);
}
.easynav-role-trigger.persona-partner.active {
  background: rgba(0, 200, 83, 0.1);
  border-color: var(--color-success);
  color: var(--color-success);
}

.role-chevron {
  font-size: 0.65rem;
  transition: transform var(--transition-normal);
  opacity: 0.7;
}

.role-chevron.rotated {
  transform: rotate(180deg);
}

.easynav-role-dropdown {
  min-width: 220px;
}

.navbar-nav-divider {
  width: 1px;
  height: 20px;
  background: var(--color-border-strong);
  align-self: center;
  margin: 0 var(--spacing-xs);
}

.dropdown-item.active {
  background: var(--color-bg-tertiary);
  font-weight: var(--font-weight-semibold);
}

.easynav-enable-btn {
  background: var(--color-bg-tertiary) !important;
  color: var(--color-text-secondary) !important;
  border-radius: var(--radius-pill);
  padding: var(--spacing-xs) var(--spacing-md) !important;
  border: 1px dashed var(--color-border-strong) !important;
  margin-left: var(--spacing-md);
  font-weight: var(--font-weight-semibold) !important;
}

.easynav-enable-btn:hover {
  border-color: var(--color-accent) !important;
  color: var(--color-accent) !important;
}

/* Home button back to paths selection */
.easynav-home-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--radius-full);
  background: var(--color-bg-tertiary);
  border: 1px solid var(--color-border);
  color: var(--color-text-secondary) !important;
  cursor: pointer;
  transition: all var(--transition-fast);
  margin-left: var(--spacing-lg);
}

.easynav-home-btn:hover {
  background: var(--color-bg-elevated);
  border-color: var(--color-accent);
  color: var(--color-accent) !important;
}

/* User Menu Section Header for Path Selection */
.dropdown-section-title {
  padding: var(--spacing-sm) var(--spacing-md) var(--spacing-xs);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Force specific colors for persona links in AppNavbar when active */
.nav-link.persona-organizer.router-link-active {
  color: var(--color-accent) !important;
}
.nav-link.persona-member.router-link-active {
  color: var(--color-persona-member) !important;
}
.nav-link.persona-partner.router-link-active {
  color: var(--color-success) !important;
}
</style>
